import { type VariablesOf } from '@graphql-typed-document-node/core'
import { useAtomValue, useSetAtom } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { brandGroupIdAtom, surveyFilterAtom } from '~/atoms'
import { graphql } from '~/gql'
import { useTabulate } from '~/hooks/useTabulate'
import { assertExists, nineNumbers } from '~/utils'

import {
  customerAttributeIdsAtom,
  fiveSegmentCommentAtom,
  isPendingTabulationAtom,
  isTabulatableAtom,
  isTabulationReadyAtom,
  lastTabulatedVariablesAtom,
  nineSegmentCommentAtom,
  questionIdsAtom,
  savedFiveSegmentCommentAtom,
  savedNineSegmentCommentAtom,
  tabulationQueryDataAtom,
} from '../atoms'

import { useSelectedBrands } from './useSelectedBrands'
import { useSurveyId } from './useSurveyId'

const questionMutationDocument = graphql(`
  mutation StandardAnalysisPage_QuestionTabulationMutation(
    $surveyId: String!
    $groupFilters: [GroupFilter!]!
    $questionIds: [String!]!
    $filter: [[CustomerAttributeFilterItemInput!]!]!
  ) {
    count: countAnswersByMultiQuestions(
      surveyId: $surveyId
      groupFilters: $groupFilters
      questionIds: $questionIds
      customFilter: { filter: $filter }
    ) {
      awaitId
    }
  }
`)
type QuestionMutationVariables = VariablesOf<typeof questionMutationDocument>

const questionQueryDocument = graphql(`
  query StandardAnalysisPage_QuestionTabulation($awaitId: String!) {
    count: countAnswersByMultiQuestions(awaitId: $awaitId) {
      arrived
      result {
        ...StandardAnalysisPage_QuestionTabulationQueryData
      }
    }
  }
`)

const customerAttributeMutationDocument = graphql(`
  mutation StandardAnalysisPage_CustomerAttributeTabulationMutation(
    $customerAttributeIds: [ID!]!
    $brands: [CountByCustomerAttributeBrandInput!]!
    $filter: [[CustomerAttributeFilterItemInput!]!]!
  ) {
    count: countByCustomerAttributes(
      input: {
        byCustomerAttributeIds: $customerAttributeIds
        brands: $brands
        customFilter: { filter: $filter }
      }
    ) {
      awaitId
    }
  }
`)
type CustomerAttributeMutationVariables = VariablesOf<
  typeof customerAttributeMutationDocument
>

const customerAttributeQueryDocument = graphql(`
  query StandardAnalysisPage_CustomerAttributeTabulation($awaitId: String!) {
    count: countByCustomerAttributes(awaitId: $awaitId) {
      arrived
      result {
        ...StandardAnalysisPage_CustomerAttributeTabulationQueryData
      }
    }
  }
`)

export const useTabulationQuery = () => {
  const { i18n } = useTranslation()
  const isTabulationReady = useAtomValue(isTabulationReadyAtom)
  const isPendingTabulation = useAtomValue(isPendingTabulationAtom)
  const isTabulatable = useAtomValue(isTabulatableAtom)

  const surveyFilter = useAtomValue(surveyFilterAtom)
  const brandGroupId = useAtomValue(brandGroupIdAtom)
  const selectedBrands = useSelectedBrands()
  const surveyId = useSurveyId()
  const questionIds = useAtomValue(questionIdsAtom)
  const customerAttributeIds = useAtomValue(customerAttributeIdsAtom)

  const [tabulateQuestionState, tabulateQuestion] = useTabulate(
    questionMutationDocument,
    questionQueryDocument,
  )
  const [tabulateCustomerAttributeState, tabulateCustomerAttribute] =
    useTabulate(
      customerAttributeMutationDocument,
      customerAttributeQueryDocument,
    )

  const setTabulationQueryData = useSetAtom(tabulationQueryDataAtom)
  const setLastTabulatedVariables = useSetAtom(lastTabulatedVariablesAtom)

  const resetFiveSegmentComment = useResetAtom(fiveSegmentCommentAtom)
  const resetSavedFiveSegmentComment = useResetAtom(savedFiveSegmentCommentAtom)
  const resetNineSegmentComment = useResetAtom(nineSegmentCommentAtom)
  const resetSavedNineSegmentComment = useResetAtom(savedNineSegmentCommentAtom)

  const hasTabulatedRef = React.useRef(false)

  const tabulateIfNeeded = React.useCallback(async () => {
    if (!isTabulationReady || isPendingTabulation || !isTabulatable) {
      return
    }

    // t を使うと言語切り替えで参照が変わり useEffect が再実行され、
    // 再集計とコメントリセットが走ってしまうため、参照が安定な i18n を使う。
    assertExists(brandGroupId, i18n.t('assertion_failed'))

    const questionVariables: QuestionMutationVariables = {
      surveyId,
      groupFilters: selectedBrands.flatMap((brand) =>
        nineNumbers.map((nineNumber) => ({
          group: [brand.id, nineNumber].join(),
          filters: [
            {
              brand_group_id: brandGroupId,
              brand_number: brand.number,
              segment_number: nineNumber,
            },
          ],
        })),
      ),
      questionIds,
      filter: surveyFilter?.filterGroups ?? [],
    }
    const customerAttributeVariables: CustomerAttributeMutationVariables = {
      customerAttributeIds,
      brands: selectedBrands.map(({ number: brandNumber }) => ({
        brandGroupId,
        brandNumber,
      })),
      filter: surveyFilter?.filterGroups ?? [],
    }
    const [questionQueryData, customerAttributeQueryData] = await Promise.all([
      tabulateQuestion(questionVariables),
      tabulateCustomerAttribute(customerAttributeVariables),
    ])

    setTabulationQueryData({
      question: questionQueryData,
      customerAttributes: customerAttributeQueryData,
    })
    setLastTabulatedVariables({
      question: questionVariables,
      customerAttributes: customerAttributeVariables,
    })

    // 再集計時は古いコメントが新しいデータに残らないようリセットする。
    // 初回集計（URL 復元したコメントを保持）ではリセットしない。
    if (hasTabulatedRef.current) {
      resetFiveSegmentComment()
      resetSavedFiveSegmentComment()
      resetNineSegmentComment()
      resetSavedNineSegmentComment()
    }
    hasTabulatedRef.current = true
  }, [
    brandGroupId,
    customerAttributeIds,
    i18n,
    isPendingTabulation,
    isTabulatable,
    isTabulationReady,
    questionIds,
    resetFiveSegmentComment,
    resetNineSegmentComment,
    resetSavedFiveSegmentComment,
    resetSavedNineSegmentComment,
    selectedBrands,
    setLastTabulatedVariables,
    setTabulationQueryData,
    surveyFilter?.filterGroups,
    surveyId,
    tabulateCustomerAttribute,
    tabulateQuestion,
  ])

  React.useEffect(() => {
    tabulateIfNeeded()
  }, [tabulateIfNeeded])

  return tabulateQuestionState.loading || tabulateCustomerAttributeState.loading
}
